"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { LogOut, CheckCircle2 } from "lucide-react";
import { getDisplayNameWithPartialEmail } from "@/lib/format-email";
import { disconnectSession } from "@/actions/session";

interface AccountConnectedCardProps {
  user?: {
    id: string;
    email: string;
    name: string | null;
    role?: string;
  } | null;
}

export function AccountConnectedCard({ user }: AccountConnectedCardProps) {
  const router = useRouter();
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  if (!user) {
    return null;
  }

  const displayName = getDisplayNameWithPartialEmail(user.name, user.email);

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectSession();
      router.refresh();
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="border border-green-200 bg-green-50 rounded-lg p-6 mb-6">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 pt-1">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-green-900 mb-1">
            Account Connected
          </h3>
          <p className="text-sm text-green-800 mb-4">
            {displayName}
          </p>
          <p className="text-xs text-green-700 mb-4">
            Your registration will be automatically linked to your Edukate Africa account.
          </p>
          <Button
            onClick={handleDisconnect}
            disabled={isDisconnecting}
            variant="outline"
            size="sm"
            className="border-green-300 text-green-700 hover:bg-green-100"
          >
            <LogOut className="h-4 w-4 mr-2" />
            {isDisconnecting ? "Disconnecting..." : "Disconnect Account"}
          </Button>
        </div>
      </div>
    </div>
  );
}
